import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Heart } from "lucide-react";
import { APP_ROUTES } from "../../routes/AppRoutes";
import discoveryImg from "../../assets/images/whatwedo/influencerMarketing/whatwedo/talentsGuests.jpg";
import strategyImg from "../../assets/images/whatwedo/influencerMarketing/whatwedo/campaignStrategy.jpg";
import managementImg from "../../assets/images/whatwedo/influencerMarketing/whatwedo/creatorManagement.jpg";
import contentImg from "../../assets/images/whatwedo/influencerMarketing/whatwedo/ugcContentCreation.jpg";
import trackingImg from "../../assets/images/whatwedo/influencerMarketing/whatwedo/performanceTracking.jpg";

const SERVICES = [
  { image: discoveryImg, title: "Creator Discovery", desc: "We shortlist creators by audience fit, engagement quality and past brand work, not just follower count." },
  { image: strategyImg, title: "Campaign Strategy", desc: "Clear goals, content angles, creator mix, timeline and budget, agreed before anything goes live." },
  { image: managementImg, title: "Creator Management", desc: "Briefing, contracts, approvals, payments and follow-ups handled by us, so you deal with one team." },
  { image: contentImg, title: "UGC & Content Creation", desc: "Reels, shorts and product videos from creators, plus AI-assisted edits and cut-downs for ads." },
  { image: trackingImg, title: "Performance Tracking", desc: "Reach, views, engagement, clicks and sales tracked per creator." },
];

const n = SERVICES.length;
// Three copies keep the row full on wide screens. Once it has slid past the
// first copy it jumps back to 0, where the identical second copy was showing.
const CARDS = [...SERVICES, ...SERVICES, ...SERVICES];

const InfluencerServices = () => {
  const [{ index, animate }, setSlide] = useState({ index: 0, animate: false });
  const [paused, setPaused] = useState(false);

  // Autoplay; restarts after every step, so an arrow click never gets doubled up.
  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = setInterval(
      () => setSlide((s) => (s.index >= n ? { index: 0, animate: false } : { index: s.index + 1, animate: true })),
      2200
    );
    return () => clearInterval(timer);
  }, [paused, index]);

  const handleSlideEnd = (e) => {
    if (e.target === e.currentTarget && index >= n) setSlide({ index: 0, animate: false });
  };

  // Past either end, jump (unseen) to the matching copy first, then slide from there.
  const jumpThenSlide = (from, to) => {
    setSlide({ index: from, animate: false });
    requestAnimationFrame(() => requestAnimationFrame(() => setSlide({ index: to, animate: true })));
  };

  const go = (dir) => {
    const next = index + dir;
    if (next < 0) jumpThenSlide(n, n - 1);
    else if (next > n) jumpThenSlide(0, 1);
    else setSlide({ index: next, animate: true });
  };

  return (
    <section className="ee-section im-services">
      <div className="container">
        <div className="im-svc-head" data-aos="fade-up" data-aos-once="true">
          <div>
            <span className="ee-eyebrow">What we do</span>
            <h2 className="ee-title">
              Everything <span className="im-heart"><Heart size={22} fill="currentColor" /></span> your{" "}
              <span>campaign needs.</span>
            </h2>
          </div>
          <div className="im-svc-aside">
            <p className="ee-intro">One team from first idea to final report. Pick only what you need, or hand us the full campaign.</p>
            <Link to={APP_ROUTES.CONTACT_US} className="im-link">
              Talk to us <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <div className="im-svc-viewport" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div
            className={`im-svc-track${animate ? " is-animating" : ""}`}
            style={{ "--i": index }}
            onTransitionEnd={handleSlideEnd}
          >
            {CARDS.map(({ image, title, desc }, i) => (
              <article
                key={i}
                className={`im-svc-item${(i % n) % 2 ? " is-low" : ""}${i < index ? " is-gone" : ""}`}
                style={{ "--d": i % n }}
                aria-hidden={i >= n}
              >
                <div className="im-svc-card">
                  <img src={image} alt="" loading="lazy" />
                  <div className="im-svc-body">
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Same round arrows as the Events page's event-type slider. */}
        <div className="ee-flow-nav">
          <button type="button" onClick={() => go(-1)} aria-label="Previous service">
            <ArrowLeft size={18} />
          </button>
          <button type="button" onClick={() => go(1)} aria-label="Next service">
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default InfluencerServices;
